import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../client/client';

// El logo de la revista lleva a la portada; el de la Cámara, a su web.
const URL_CAMARA = 'https://www.camara-arequipa.org.pe/';

const NewNavbar = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [openSearch, setOpenSearch] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [menuAbierto, setMenuAbierto] = useState(false);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const fetchCategories = async () => {
      const { data, error } = await supabase
        .from('categorias')
        .select('id, name')
        .order('id');

      if (!error) {
        setCategories(data);
      }
    };

    fetchCategories();
  }, []);

  // Al cambiar de página el menú desplegable debe cerrarse solo.
  useEffect(() => {
    setMenuAbierto(false);
  }, [pathname]);

  // Con el menú abierto se bloquea el desplazamiento del fondo.
  useEffect(() => {
    document.body.style.overflow = menuAbierto ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuAbierto]);

  const handleSearch = () => {
    if (!searchTerm.trim()) return;
    setMenuAbierto(false);
    navigate(`/busqueda/${encodeURIComponent(searchTerm.trim())}`);
  };

  const esActiva = (ruta) => (pathname === ruta ? 'active' : '');

  const secciones = [
    { ruta: '/nosotros', nombre: 'Nosotros' },
    { ruta: '/editorial', nombre: 'Editorial' },
    ...categories.map((c) => ({ ruta: `/categorias/${c.id}`, nombre: c.name })),
    { ruta: '/anuncia', nombre: 'Anuncia con Nosotros' },
  ];

  return (
    <>
      <nav className="navbar">
        <div className="container">
          {/* Solo se muestra por debajo del punto de ruptura, donde se oculta
              la fila de secciones. */}
          <div className="left">
            <button
              type="button"
              className="ed-hamburguesa"
              aria-label={menuAbierto ? 'Cerrar menú' : 'Abrir menú'}
              aria-expanded={menuAbierto}
              onClick={() => setMenuAbierto((v) => !v)}
            >
              {menuAbierto ? (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}>
                  <path strokeLinecap="round" d="M6 18 18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}>
                  <path strokeLinecap="round" d="M3.75 6.75h16.5M3.75 12h16.5M3.75 17.25h16.5" />
                </svg>
              )}
            </button>
          </div>

          {/* Cabecera: el nombre de la revista y, junto a él, el escudo de la
              institución que la edita. */}
          <div className="center">
            <div className="ed-cabecera-logos">
              <Link to="/" className="ed-cabecera-enlace" aria-label="Ir a la portada">
                <img
                  src="/logo_revista.webp"
                  className="logo"
                  alt="Cámara AQP, revista institucional de la CCIA"
                />
              </Link>
              <span className="ed-cabecera-sep" aria-hidden="true" />
              <a
                href={URL_CAMARA}
                target="_blank"
                rel="noopener noreferrer"
                className="ed-cabecera-enlace"
                aria-label="Ir a la web de la Cámara de Comercio e Industria de Arequipa"
              >
                <img
                  src="/logo_ccia.webp"
                  className="logo2"
                  alt="Cámara de Comercio e Industria de Arequipa"
                />
              </a>
            </div>
          </div>

          <div className="right">
            {/* En pantallas anchas la caja está siempre a la vista; en las
                estrechas se mantiene el icono para no comerse la cabecera. */}
            <div className="search-container ed-buscador-fijo">
              <input
                type="text"
                name="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSearch();
                  }
                }}
                className="input-search"
                placeholder="Buscar en la revista..."
                aria-label="Buscar en la revista"
              />
              <button
                type="button"
                className="ed-icono-boton"
                aria-label="Buscar"
                onClick={handleSearch}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
                  />
                </svg>
              </button>
            </div>

            {openSearch ? (
              <div className="search-container ed-buscador-movil">
                <input
                  type="text"
                  name="search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSearch();
                    }
                  }}
                  className="input-search"
                  placeholder="Buscar..."
                  autoFocus
                />
                <button
                  type="button"
                  className="ed-icono-boton"
                  aria-label="Cerrar buscador"
                  onClick={() => setOpenSearch(false)}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ) : (
              <button
                type="button"
                className="ed-icono-boton ed-buscador-movil"
                aria-label="Buscar"
                onClick={() => setOpenSearch(true)}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* Fila de secciones: visible en pantallas anchas */}
      <nav className="sub-navbar">
        <div className="container">
          <ul className="options">
            {secciones.map(({ ruta, nombre }) => (
              <li key={ruta}>
                <Link to={ruta} className={esActiva(ruta)}>
                  {nombre}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </nav>

      {/* Panel desplegable: sustituye a la fila de secciones en pantallas estrechas */}
      {menuAbierto && (
        <>
          <div className="ed-menu-fondo" onClick={() => setMenuAbierto(false)} />
          <div className="ed-menu" role="dialog" aria-label="Menú de secciones">
            <ul>
              {secciones.map(({ ruta, nombre }) => (
                <li key={ruta}>
                  <Link to={ruta} className={esActiva(ruta)}>
                    {nombre}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </>
  );
};

export default NewNavbar;
